import { useEffect, useMemo, useRef, useState } from "react";
import {
  buildRouteProjection,
  type RawRouteGeometry,
  type RouteProjection,
} from "./routeProjection";
import type { HistoryStore } from "./positionHistory";
import type { Vehicle } from "./useVehicleStream";
import "./BingoCard.css";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type Props = {
  vehicles: Vehicle[];
  history: HistoryStore;
  historyVersion: number;
  connected: boolean;
  historical?: boolean;
};

// ---------------------------------------------------------------------------
// Storage keys
// ---------------------------------------------------------------------------

const SEED_KEY = "link.bingo.seed";
const marksKey = (seed: number) => `link.bingo.marks.${seed}`;
// Per-seed suppression set: predicate IDs that were firing at reset time and
// should be ignored until they go false at least once. Without this, hitting
// Reset would instantly re-mark cells that happen to be true right now (e.g.
// a train is currently at Westlake), which feels like "I already won" on a
// supposedly fresh card.
const suppressKey = (seed: number) => `link.bingo.suppress.${seed}`;

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

const EARTH_R = 6371000;
const DEG2RAD = Math.PI / 180;

function haversineM(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const dLat = (lat2 - lat1) * DEG2RAD;
  const dLon = (lon2 - lon1) * DEG2RAD;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * DEG2RAD) *
      Math.cos(lat2 * DEG2RAD) *
      Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_R * Math.asin(Math.min(1, Math.sqrt(a)));
}

// ---------------------------------------------------------------------------
// Stop directory: a flat list of stops with lat/lon, derived from the raw
// geometry payload (which has them per-route) before we discard the polyline.
// ---------------------------------------------------------------------------

type StopLoc = {
  id: string;
  name: string;
  lat: number;
  lon: number;
};

type Geom = {
  byRouteId: Map<string, RouteProjection>;
  stops: StopLoc[];
  /** Lower-cased name -> first matching stop. */
  stopsByName: Map<string, StopLoc>;
};

function buildGeom(raw: RawRouteGeometry[]): Geom {
  const byRouteId = new Map<string, RouteProjection>();
  for (const r of raw) {
    byRouteId.set(r.route_id, buildRouteProjection(r));
  }
  const seen = new Set<string>();
  const stops: StopLoc[] = [];
  for (const r of raw) {
    for (const s of r.stops) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      stops.push({ id: s.id, name: s.name, lat: s.lat, lon: s.lon });
    }
  }
  const stopsByName = new Map<string, StopLoc>();
  for (const s of stops) stopsByName.set(s.name.toLowerCase(), s);
  return { byRouteId, stops, stopsByName };
}

// findStop does a fuzzy case-insensitive contains-match against stop names,
// preferring an exact match if one exists. Returns the first hit so callers
// can use loose handles like "beacon hill" or "westlake".
function findStop(geom: Geom | null, query: string): StopLoc | null {
  if (!geom) return null;
  const q = query.toLowerCase();
  const exact = geom.stopsByName.get(q);
  if (exact) return exact;
  for (const s of geom.stops) {
    if (s.name.toLowerCase().includes(q)) return s;
  }
  return null;
}

// nearestStop returns the closest stop to (lat,lon) and its distance (m).
function nearestStop(
  geom: Geom | null,
  lat: number,
  lon: number,
): { stop: StopLoc; distM: number } | null {
  if (!geom || geom.stops.length === 0) return null;
  let best: StopLoc = geom.stops[0];
  let bestD = haversineM(lat, lon, best.lat, best.lon);
  for (let i = 1; i < geom.stops.length; i++) {
    const s = geom.stops[i];
    const d = haversineM(lat, lon, s.lat, s.lon);
    if (d < bestD) {
      bestD = d;
      best = s;
    }
  }
  return { stop: best, distM: bestD };
}

// ---------------------------------------------------------------------------
// Route helpers
// ---------------------------------------------------------------------------

const ROUTE_1 = "40_100479";
const ROUTE_2 = "40_2LINE";

function isLine1(v: Vehicle): boolean {
  return v.route_id === ROUTE_1 || v.route_short_name === "1 Line";
}
function isLine2(v: Vehicle): boolean {
  return v.route_id === ROUTE_2 || v.route_short_name === "2 Line";
}

// ---------------------------------------------------------------------------
// State passed to predicates. Computed once per evaluation pass so each
// predicate doesn't have to recompute common derivations.
// ---------------------------------------------------------------------------

type BingoState = {
  vehicles: Vehicle[];
  history: HistoryStore;
  geom: Geom | null;
  nowSec: number;
};

// vehiclesNearStop returns vehicles whose lat/lon is within `radiusM` of the
// named stop. Returns [] if geometry isn't loaded yet — predicates that need
// it will simply not fire until it does.
function vehiclesNearStop(
  s: BingoState,
  stopName: string,
  radiusM: number,
  filter?: (v: Vehicle) => boolean,
): Vehicle[] {
  const stop = findStop(s.geom, stopName);
  if (!stop) return [];
  const out: Vehicle[] = [];
  for (const v of s.vehicles) {
    if (filter && !filter(v)) continue;
    if (haversineM(v.lat, v.lon, stop.lat, stop.lon) <= radiusM) out.push(v);
  }
  return out;
}

// dwellingAtStop returns true if any vehicle has the last several samples all
// within `radiusM` of the same stop, spanning at least `minSec` of dwell time.
function dwellingAtStop(s: BingoState, minSec: number, radiusM = 60): boolean {
  if (!s.geom) return false;
  for (const entry of s.history.byVehicle.values()) {
    const ss = entry.samples;
    if (ss.length < 2) continue;
    const last = ss[ss.length - 1];
    const ns = nearestStop(s.geom, last.lat, last.lon);
    if (!ns || ns.distM > radiusM) continue;
    // Walk backwards while we're still near the same stop.
    let i = ss.length - 1;
    while (i > 0) {
      const p = ss[i - 1];
      const d = haversineM(p.lat, p.lon, ns.stop.lat, ns.stop.lon);
      if (d > radiusM) break;
      i--;
    }
    const span = last.ts - ss[i].ts;
    if (span >= minSec) return true;
  }
  return false;
}

// effectiveSpeedMps: vehicle.speed if reported, else estimate from the last
// two history samples. Returns 0 if we can't tell.
function effectiveSpeedMps(s: BingoState, v: Vehicle): number {
  if (typeof v.speed === "number" && v.speed > 0) return v.speed;
  const entry = s.history.byVehicle.get(v.vehicle_id);
  if (!entry || entry.samples.length < 2) return 0;
  const a = entry.samples[entry.samples.length - 2];
  const b = entry.samples[entry.samples.length - 1];
  const dt = b.ts - a.ts;
  if (dt <= 0) return 0;
  const d = haversineM(a.lat, a.lon, b.lat, b.lon);
  return d / dt;
}

// switchedDirection returns true if any vehicle's tripHeadsign changed across
// recent history samples (e.g. flipped from Lynnwood to Angle Lake).
function switchedDirection(s: BingoState): boolean {
  for (const entry of s.history.byVehicle.values()) {
    const seen = new Set<string>();
    for (const sample of entry.samples) {
      if (sample.tripHeadsign) seen.add(sample.tripHeadsign);
      if (seen.size >= 2) return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// Predicate registry. IDs are stable — saved marks survive code edits as long
// as we don't change a predicate's id. Reorder/add freely.
// ---------------------------------------------------------------------------

type Predicate = {
  id: string;
  label: string;
  check: (s: BingoState) => boolean;
};

const PREDICATES: Predicate[] = [
  {
    id: "seatac-three",
    label: "🚉 Three trains at SeaTac at once",
    check: (s) =>
      vehiclesNearStop(s, "seatac", 250).length >= 3 ||
      vehiclesNearStop(s, "airport", 250).length >= 3,
  },
  {
    id: "dwell-60s",
    label: "🐌 A train sits at any station > 60s",
    check: (s) => dwellingAtStop(s, 60),
  },
  {
    id: "dwell-3m",
    label: "😴 A train dwells at a station > 3 min",
    check: (s) => dwellingAtStop(s, 180),
  },
  {
    id: "westlake-1line",
    label: "🚆 1 Line train spotted at Westlake",
    check: (s) => vehiclesNearStop(s, "westlake", 200, isLine1).length > 0,
  },
  {
    id: "bellevue-2line",
    label: "🚆 2 Line train spotted at Bellevue Downtown",
    check: (s) =>
      vehiclesNearStop(s, "bellevue downtown", 250, isLine2).length > 0 ||
      vehiclesNearStop(s, "bellevue", 250, isLine2).length > 0,
  },
  {
    id: "i90-bridge",
    label: "🌉 Train crosses Lake Washington",
    check: (s) =>
      s.vehicles.some(
        (v) =>
          isLine2(v) &&
          v.lat > 47.575 &&
          v.lat < 47.605 &&
          v.lon > -122.275 &&
          v.lon < -122.21,
      ),
  },
  {
    id: "speed-50mph",
    label: "💨 Any train moving > 50 mph",
    check: (s) => s.vehicles.some((v) => effectiveSpeedMps(s, v) > 22.35),
  },
  {
    id: "speed-35mph",
    label: "🏃 A train cruising > 35 mph",
    check: (s) => s.vehicles.some((v) => effectiveSpeedMps(s, v) > 15.6),
  },
  {
    id: "stopped",
    label: "🛑 A train stopped (< 1 mph)",
    check: (s) =>
      s.vehicles.some((v) => {
        const sp = effectiveSpeedMps(s, v);
        // Only count "stopped" if we actually have a speed signal — otherwise
        // every vehicle that's never moved looks stopped.
        return (
          sp < 0.45 &&
          (typeof v.speed === "number" ||
            (s.history.byVehicle.get(v.vehicle_id)?.samples.length ?? 0) >= 2)
        );
      }),
  },
  {
    id: "capitol-hill-nb-1line",
    label: "🌃 Northbound 1 Line train at Capitol Hill",
    check: (s) =>
      vehiclesNearStop(s, "capitol hill", 250, isLine1).some((v) => {
        if (typeof v.bearing === "number") {
          // Northbound-ish: bearing within ±60° of true north.
          const b = ((v.bearing % 360) + 360) % 360;
          if (b < 60 || b > 300) return true;
        }
        return /lynnwood|northgate/i.test(v.trip_headsign ?? "");
      }),
  },
  {
    id: "seatac-1line",
    label: "🛬 Plane spotter: 1 Line train at SeaTac/Airport",
    check: (s) =>
      vehiclesNearStop(s, "seatac", 250, isLine1).length > 0 ||
      vehiclesNearStop(s, "airport", 250, isLine1).length > 0,
  },
  {
    id: "id-42",
    label: "🦆 Vehicle ID contains '42'",
    check: (s) => s.vehicles.some((v) => /42/.test(v.vehicle_id)),
  },
  {
    id: "id-palindrome",
    label: "✨ Vehicle ID is a palindrome",
    check: (s) =>
      s.vehicles.some((v) => {
        const digits = v.vehicle_id.replace(/\D/g, "");
        return digits.length >= 3 && digits === digits.split("").reverse().join("");
      }),
  },
  {
    id: "id-ends-00",
    label: "🎯 Vehicle ID ends in 00",
    check: (s) => s.vehicles.some((v) => /00$/.test(v.vehicle_id)),
  },
  {
    id: "id-triple",
    label: "🎰 Vehicle ID has three matching digits",
    check: (s) =>
      s.vehicles.some((v) => /(\d)\1\1/.test(v.vehicle_id)),
  },
  {
    id: "two-2line",
    label: "🚆🚆 Two 2 Line trains in service",
    check: (s) => s.vehicles.filter(isLine2).length >= 2,
  },
  {
    id: "three-1line-cluster",
    label: "🚆🚆🚆 Three 1 Line trains within 1 station of each other",
    check: (s) => {
      const proj = s.geom?.byRouteId.get(ROUTE_1);
      if (!proj) return false;
      const dists: number[] = [];
      for (const v of s.vehicles) {
        if (!isLine1(v)) continue;
        const d = proj.project(v.lat, v.lon);
        if (d !== null) dists.push(d);
      }
      dists.sort((a, b) => a - b);
      for (let i = 0; i + 2 < dists.length; i++) {
        if (dists[i + 2] - dists[i] <= 1500) return true;
      }
      return false;
    },
  },
  {
    id: "all-headsigns",
    label: "🤖 All known headsigns appear simultaneously",
    check: (s) => {
      const want = [/lynnwood/i, /federal way|angle lake/i, /redmond/i];
      return want.every((re) =>
        s.vehicles.some((v) => re.test(v.trip_headsign ?? "")),
      );
    },
  },
  {
    id: "beacon-hill",
    label: "🎢 Train at Beacon Hill (deepest station)",
    check: (s) => vehiclesNearStop(s, "beacon hill", 250).length > 0,
  },
  {
    id: "intl-district",
    label: "🐟 Train at Int'l District/Chinatown",
    check: (s) =>
      vehiclesNearStop(s, "international district", 250).length > 0 ||
      vehiclesNearStop(s, "chinatown", 250).length > 0,
  },
  {
    id: "u-district",
    label: "🌳 Train at U District",
    check: (s) => vehiclesNearStop(s, "u district", 250).length > 0,
  },
  {
    id: "pioneer-square",
    label: "🏛️ Train at Pioneer Square",
    check: (s) => vehiclesNearStop(s, "pioneer square", 250).length > 0,
  },
  {
    id: "northgate",
    label: "🛒 Train at Northgate",
    check: (s) => vehiclesNearStop(s, "northgate", 250).length > 0,
  },
  {
    id: "uw",
    label: "📚 Train at University of Washington",
    check: (s) =>
      vehiclesNearStop(s, "university of washington", 250).length > 0,
  },
  {
    id: "fleet-15",
    label: "🏙️ ≥ 15 trains in service",
    check: (s) => s.vehicles.length >= 15,
  },
  {
    id: "fleet-low",
    label: "🌙 ≤ 5 trains in service",
    check: (s) => s.vehicles.length > 0 && s.vehicles.length <= 5,
  },
  {
    id: "1line-coverage",
    label: "🟢 1 Line full coverage: 5+ trains",
    check: (s) => s.vehicles.filter(isLine1).length >= 5,
  },
  {
    id: "2line-coverage",
    label: "🔵 2 Line full coverage: 4+ trains",
    check: (s) => s.vehicles.filter(isLine2).length >= 4,
  },
  {
    id: "top-of-hour",
    label: "⏰ Saw a train at exactly :00 of an hour",
    check: (s) =>
      s.vehicles.some((v) => v.ts > 0 && v.ts % 3600 === 0),
  },
  {
    id: "switched-dir",
    label: "🔁 A train switched directions",
    check: (s) => switchedDirection(s),
  },
  {
    id: "shared-stop",
    label: "🎲 Two trains share the same nearest stop",
    check: (s) => {
      if (!s.geom) return false;
      const counts = new Map<string, number>();
      for (const v of s.vehicles) {
        const ns = nearestStop(s.geom, v.lat, v.lon);
        if (!ns || ns.distM > 400) continue;
        const c = (counts.get(ns.stop.id) ?? 0) + 1;
        counts.set(ns.stop.id, c);
        if (c >= 2) return true;
      }
      return false;
    },
  },
  {
    id: "lynnwood-headsign",
    label: "🧭 A train bound for Lynnwood",
    check: (s) =>
      s.vehicles.some((v) => /lynnwood/i.test(v.trip_headsign ?? "")),
  },
  {
    id: "redmond-headsign",
    label: "🧭 A train bound for Redmond",
    check: (s) =>
      s.vehicles.some((v) => /redmond/i.test(v.trip_headsign ?? "")),
  },
  {
    id: "angle-lake-headsign",
    label: "🧭 A train bound for Angle Lake/Federal Way",
    check: (s) =>
      s.vehicles.some((v) =>
        /angle lake|federal way/i.test(v.trip_headsign ?? ""),
      ),
  },
  {
    id: "both-lines",
    label: "🤝 Both 1 Line and 2 Line in service",
    check: (s) =>
      s.vehicles.some(isLine1) && s.vehicles.some(isLine2),
  },
  {
    id: "downtown-pair",
    label: "🏙️ A train at Westlake and one at Pioneer Square",
    check: (s) =>
      vehiclesNearStop(s, "westlake", 250).length > 0 &&
      vehiclesNearStop(s, "pioneer square", 250).length > 0,
  },
  {
    id: "south-of-seatac",
    label: "🛫 Train south of SeaTac (Angle Lake/Federal Way territory)",
    check: (s) => s.vehicles.some((v) => v.lat < 47.43),
  },
  {
    id: "north-of-northgate",
    label: "🌲 Train north of Northgate",
    check: (s) => s.vehicles.some((v) => v.lat > 47.72),
  },
  {
    id: "tunnel-cluster",
    label: "🕳️ Three trains in the downtown tunnel",
    check: (s) =>
      s.vehicles.filter(
        (v) =>
          v.lat > 47.595 &&
          v.lat < 47.615 &&
          v.lon > -122.34 &&
          v.lon < -122.32,
      ).length >= 3,
  },
  {
    id: "id-sequential",
    label: "🔢 Two vehicles with consecutive IDs",
    check: (s) => {
      const nums = s.vehicles
        .map((v) => parseInt(v.vehicle_id.replace(/\D/g, ""), 10))
        .filter((n) => Number.isFinite(n))
        .sort((a, b) => a - b);
      for (let i = 1; i < nums.length; i++) {
        if (nums[i] - nums[i - 1] === 1) return true;
      }
      return false;
    },
  },
  {
    id: "fast-and-stopped",
    label: "⚖️ One train flying, another at rest",
    check: (s) => {
      let hasFast = false;
      let hasStopped = false;
      for (const v of s.vehicles) {
        const sp = effectiveSpeedMps(s, v);
        if (sp > 13) hasFast = true;
        if (
          sp < 0.45 &&
          (typeof v.speed === "number" ||
            (s.history.byVehicle.get(v.vehicle_id)?.samples.length ?? 0) >= 2)
        )
          hasStopped = true;
        if (hasFast && hasStopped) return true;
      }
      return false;
    },
  },
];

// ---------------------------------------------------------------------------
// Seeded RNG (mulberry32) for stable per-day card layouts.
// ---------------------------------------------------------------------------

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function todaySeed(): number {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return Number(`${y}${m}${day}`);
}

function pickPredicates(seed: number): Predicate[] {
  const rng = mulberry32(seed);
  const pool = [...PREDICATES];
  // Fisher–Yates with the seeded RNG so the same seed always yields the same
  // 24 cells in the same order.
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, 24);
}

// ---------------------------------------------------------------------------
// Cell layout: 5×5 with the center (index 12) reserved for FREE.
// ---------------------------------------------------------------------------

const FREE_INDEX = 12; // (2,2)

type Cell =
  | { kind: "free" }
  | { kind: "predicate"; predicate: Predicate };

function buildGrid(seed: number): Cell[] {
  const preds = pickPredicates(seed);
  const cells: Cell[] = [];
  let pi = 0;
  for (let i = 0; i < 25; i++) {
    if (i === FREE_INDEX) {
      cells.push({ kind: "free" });
    } else {
      cells.push({ kind: "predicate", predicate: preds[pi++] });
    }
  }
  return cells;
}

// ---------------------------------------------------------------------------
// Bingo line detection.
// ---------------------------------------------------------------------------

type Line = { name: string; indices: number[] };

const LINES: Line[] = (() => {
  const ls: Line[] = [];
  for (let r = 0; r < 5; r++) {
    const row: number[] = [];
    for (let c = 0; c < 5; c++) row.push(r * 5 + c);
    const name = ["Top row", "Second row", "Middle row", "Fourth row", "Bottom row"][r];
    ls.push({ name, indices: row });
  }
  for (let c = 0; c < 5; c++) {
    const col: number[] = [];
    for (let r = 0; r < 5; r++) col.push(r * 5 + c);
    const name = ["Left column", "Second column", "Middle column", "Fourth column", "Right column"][c];
    ls.push({ name, indices: col });
  }
  ls.push({ name: "Diagonal ↘", indices: [0, 6, 12, 18, 24] });
  ls.push({ name: "Diagonal ↙", indices: [4, 8, 12, 16, 20] });
  return ls;
})();

function completedLines(marked: Set<string>, cells: Cell[]): Line[] {
  return LINES.filter((line) =>
    line.indices.every((i) => {
      const cell = cells[i];
      if (cell.kind === "free") return true;
      return marked.has(cell.predicate.id);
    }),
  );
}

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------

function loadSeed(): number {
  try {
    const raw = localStorage.getItem(SEED_KEY);
    if (raw) {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return n;
    }
  } catch {
    // ignore
  }
  const seed = todaySeed();
  try {
    localStorage.setItem(SEED_KEY, String(seed));
  } catch {
    // ignore
  }
  return seed;
}

function loadMarks(seed: number): Set<string> {
  try {
    const raw = localStorage.getItem(marksKey(seed));
    if (raw) {
      const arr = JSON.parse(raw) as unknown;
      if (Array.isArray(arr)) {
        return new Set(arr.filter((x): x is string => typeof x === "string"));
      }
    }
  } catch {
    // ignore
  }
  return new Set();
}

function saveMarks(seed: number, marks: Set<string>) {
  try {
    localStorage.setItem(marksKey(seed), JSON.stringify(Array.from(marks)));
  } catch {
    // ignore
  }
}

// Suppression set persistence. `null` is a sentinel meaning "baseline pending"
// — the next predicate-evaluation pass should snapshot which predicates are
// currently firing and stash them here, without marking anything. We persist
// the resolved set so a reload after reset keeps the same baseline.
function loadSuppressed(seed: number): Set<string> | null {
  try {
    const raw = localStorage.getItem(suppressKey(seed));
    if (raw === null) return new Set();
    if (raw === "pending") return null;
    const arr = JSON.parse(raw) as unknown;
    if (Array.isArray(arr)) {
      return new Set(arr.filter((x): x is string => typeof x === "string"));
    }
  } catch {
    // ignore
  }
  return new Set();
}

function saveSuppressed(seed: number, suppressed: Set<string> | null) {
  try {
    if (suppressed === null) {
      localStorage.setItem(suppressKey(seed), "pending");
    } else {
      localStorage.setItem(
        suppressKey(seed),
        JSON.stringify(Array.from(suppressed)),
      );
    }
  } catch {
    // ignore
  }
}

function clearSuppressed(seed: number) {
  try {
    localStorage.removeItem(suppressKey(seed));
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Confetti: a handful of absolutely-positioned divs animated outward via CSS.
// ---------------------------------------------------------------------------

const CONFETTI_COUNT = 36;
const CONFETTI_EMOJI = ["🎉", "🚆", "🎊", "✨", "🎟️", "🟡", "🔵", "🟢"];

function Confetti({ seed }: { seed: number }) {
  // The seed is just used to mix up the angles per-burst so successive bingos
  // don't look identical.
  const rng = useMemo(() => mulberry32(seed), [seed]);
  const pieces = useMemo(
    () =>
      Array.from({ length: CONFETTI_COUNT }, (_, i) => {
        const angle = rng() * Math.PI * 2;
        const dist = 120 + rng() * 220;
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist;
        const rot = (rng() - 0.5) * 720;
        const delay = rng() * 0.15;
        const emoji = CONFETTI_EMOJI[i % CONFETTI_EMOJI.length];
        return { dx, dy, rot, delay, emoji, key: i };
      }),
    [rng],
  );
  return (
    <div className="bingo-confetti" aria-hidden>
      {pieces.map((p) => (
        <span
          key={p.key}
          className="bingo-confetti-piece"
          style={{
            // Custom properties consumed by the @keyframes in the stylesheet.
            ["--dx" as string]: `${p.dx}px`,
            ["--dy" as string]: `${p.dy}px`,
            ["--rot" as string]: `${p.rot}deg`,
            animationDelay: `${p.delay}s`,
          }}
        >
          {p.emoji}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function BingoCard({
  vehicles,
  history,
  historyVersion,
  connected,
  historical,
}: Props) {
  const [seed, setSeed] = useState<number>(() => loadSeed());
  const [marks, setMarks] = useState<Set<string>>(() => loadMarks(seed));
  // Predicates suppressed because they were already firing at reset time.
  // `null` means "snapshot pending" — the next eval pass will populate it.
  const [suppressed, setSuppressed] = useState<Set<string> | null>(() =>
    loadSuppressed(seed),
  );
  // newlyMarked: ids that just fired, so we can play the stamp animation
  // without re-triggering it on every render.
  const [newlyMarked, setNewlyMarked] = useState<Set<string>>(() => new Set());
  const [geom, setGeom] = useState<Geom | null>(null);
  const [confettiKey, setConfettiKey] = useState<number | null>(null);
  // Track which line-completion state we've already celebrated, so the
  // confetti doesn't loop forever once you hit BINGO.
  const celebratedRef = useRef<string>("");

  const cells = useMemo(() => buildGrid(seed), [seed]);

  // Fetch route geometry once; failures are non-fatal — predicates that need
  // it will simply abstain.
  useEffect(() => {
    const ctl = new AbortController();
    (async () => {
      try {
        const res = await fetch("/api/routes/geometry", { signal: ctl.signal });
        if (!res.ok) return;
        const data = (await res.json()) as RawRouteGeometry[];
        setGeom(buildGeom(data));
      } catch {
        // ignore
      }
    })();
    return () => ctl.abort();
  }, []);

  // Evaluate predicates whenever the data snapshot changes.
  useEffect(() => {
    const state: BingoState = {
      vehicles,
      history,
      geom,
      nowSec: Math.floor(Date.now() / 1000),
    };
    const firing: string[] = [];
    for (const cell of cells) {
      if (cell.kind !== "predicate") continue;
      const id = cell.predicate.id;
      if (marks.has(id)) continue;
      let fired = false;
      try {
        fired = cell.predicate.check(state);
      } catch {
        // Predicate threw — pretend it didn't fire so we keep going. This is
        // intentionally swallowed: a buggy predicate shouldn't break the card.
      }
      if (fired) firing.push(id);
    }

    // Baseline pending (just after a reset): record currently-firing
    // predicates as suppressed and don't mark anything this pass. Those
    // predicates have to go false and then fire again before they count.
    if (suppressed === null) {
      const baseline = new Set(firing);
      setSuppressed(baseline);
      saveSuppressed(seed, baseline);
      return;
    }

    // Clear suppression for any predicates that aren't currently firing —
    // they've "reset" and a future firing should count.
    let nextSuppressed: Set<string> | null = null;
    if (suppressed.size > 0) {
      const firingSet = new Set(firing);
      for (const id of suppressed) {
        if (!firingSet.has(id)) {
          if (!nextSuppressed) nextSuppressed = new Set(suppressed);
          nextSuppressed.delete(id);
        }
      }
    }

    const fresh = firing.filter((id) => !suppressed.has(id));

    if (nextSuppressed) {
      setSuppressed(nextSuppressed);
      saveSuppressed(seed, nextSuppressed);
    }
    if (fresh.length === 0) return;
    setMarks((prev) => {
      const next = new Set(prev);
      for (const id of fresh) next.add(id);
      saveMarks(seed, next);
      return next;
    });
    setNewlyMarked((prev) => {
      const next = new Set(prev);
      for (const id of fresh) next.add(id);
      return next;
    });
    // Clear the "newly marked" decoration after the stamp animation is done.
    const t = window.setTimeout(() => {
      setNewlyMarked((prev) => {
        const next = new Set(prev);
        for (const id of fresh) next.delete(id);
        return next;
      });
    }, 900);
    return () => window.clearTimeout(t);
    // We intentionally key off historyVersion + vehicles.length + geom so we
    // re-evaluate at the cadence of the upstream stream rather than on every
    // parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyVersion, vehicles, geom, seed, cells, suppressed]);

  const completed = useMemo(() => completedLines(marks, cells), [marks, cells]);
  const completedKey = completed.map((l) => l.name).join("|");

  // Fire confetti when the set of completed lines grows (i.e. the user just
  // hit a new BINGO). We don't celebrate again if a line was already
  // complete on mount (e.g. you reload after winning).
  useEffect(() => {
    if (completed.length === 0) {
      celebratedRef.current = "";
      return;
    }
    if (completedKey === celebratedRef.current) return;
    const wasEmpty = celebratedRef.current === "";
    celebratedRef.current = completedKey;
    if (wasEmpty && marks.size <= cells.filter((c) => c.kind === "predicate").length) {
      // First evaluation after mount — don't auto-confetti for already-won
      // cards, only for fresh wins. We detect "first eval" by checking if
      // newlyMarked is empty.
      if (newlyMarked.size === 0) return;
    }
    setConfettiKey(Date.now());
    const t = window.setTimeout(() => setConfettiKey(null), 2200);
    return () => window.clearTimeout(t);
  }, [completedKey, completed.length, newlyMarked.size, marks.size, cells]);

  const markedCount = marks.size; // includes nothing for FREE — we count the FREE separately in display
  const totalPredicateCells = 24;

  function resetCard() {
    const ok = window.confirm(
      "Re-roll the bingo card? Your current marks will be cleared.",
    );
    if (!ok) return;
    const next = Date.now();
    try {
      localStorage.setItem(SEED_KEY, String(next));
      localStorage.removeItem(marksKey(seed));
      clearSuppressed(seed);
      // Persist the pending sentinel so a reload mid-baseline still suppresses.
      saveSuppressed(next, null);
    } catch {
      // ignore
    }
    setMarks(new Set());
    setNewlyMarked(new Set());
    setSuppressed(null);
    celebratedRef.current = "";
    setConfettiKey(null);
    setSeed(next);
  }

  return (
    <div className="bingo-root">
      <div className="bingo-card">
        <header className="bingo-header">
          <div className="bingo-title-row">
            <h1 className="bingo-title">🚆 LINK BINGO</h1>
            <button
              type="button"
              className="bingo-reset"
              onClick={resetCard}
              title="Re-roll the card"
            >
              ↻ Reset card
            </button>
          </div>
          <div className="bingo-subtitle">
            <span className="bingo-card-num">Card #{seed}</span>
            <span className="bingo-progress">
              {markedCount}/{totalPredicateCells} marked
            </span>
            <span
              className={`bingo-conn ${connected ? "ok" : "wait"}`}
              title={connected ? "Live" : "Reconnecting"}
            >
              {connected ? "● live" : "○ offline"}
            </span>
            {historical && (
              <span className="bingo-historical" title="Viewing historical data">
                ⏳ history
              </span>
            )}
          </div>
        </header>

        <div className="bingo-grid" role="grid" aria-label="Transit bingo card">
          {cells.map((cell) => {
            if (cell.kind === "free") {
              return (
                <div
                  key="free"
                  className="bingo-cell bingo-cell-free marked"
                  role="gridcell"
                  aria-label="Free space"
                >
                  <div className="bingo-cell-icon" aria-hidden>
                    🚆
                  </div>
                  <div className="bingo-cell-label">FREE</div>
                  <div className="bingo-stamp bingo-stamp-static" aria-hidden>
                    FREE!
                  </div>
                </div>
              );
            }
            const id = cell.predicate.id;
            const isMarked = marks.has(id);
            const justMarked = newlyMarked.has(id);
            return (
              <div
                key={id}
                className={`bingo-cell ${isMarked ? "marked" : ""} ${
                  justMarked ? "just-marked" : ""
                }`}
                role="gridcell"
                aria-label={cell.predicate.label}
                title={cell.predicate.label}
              >
                <div className="bingo-cell-label">{cell.predicate.label}</div>
                {isMarked && (
                  <div className="bingo-stamp" aria-hidden>
                    ✓ SEEN
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {completed.length > 0 && (
          <div className="bingo-banner" role="status">
            <div className="bingo-banner-headline">🎉 BINGO! 🚆 🎉</div>
            <div className="bingo-banner-detail">
              {completed.length === 1
                ? `${completed[0].name} complete.`
                : `${completed.length} lines complete: ${completed
                    .map((l) => l.name)
                    .join(", ")}.`}
            </div>
          </div>
        )}

        <footer className="bingo-footer">
          Card auto-marks as conditions appear in live train data. Marks for
          card #{seed} are saved in your browser. Reset to re-roll.
        </footer>
      </div>

      {confettiKey !== null && <Confetti seed={confettiKey} />}
    </div>
  );
}
